import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { COLORS, GRADIENTS, SHADOWS, Icons, SparkleBadge } from './ui/crm-ui';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface ChatResponse {
  response: string;
  promptName: string;
  tokens: number;
}

interface Props {
  promptId: number;
  promptName: string;
  baseApiUrl: string;
  onClose: () => void;
}

function apiError(err: unknown): string {
  const e = err as { response?: { data?: { error?: string } }; message?: string };
  return e.response?.data?.error ?? e.message ?? 'An error occurred';
}

// ─── Lightweight markdown renderer ──────────────────────────────────────────

function renderInline(text: string): React.ReactNode {
  // Match **bold**, *italic*, `code` in priority order (bold before italic)
  const pattern = /(\*\*(.+?)\*\*)|(\*(.+?)\*)|(`(.+?)`)/g;
  const result: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) result.push(text.slice(lastIndex, match.index));
    if (match[1]) {
      result.push(<strong key={key++} style={{ fontWeight: 700 }}>{match[2]}</strong>);
    } else if (match[3]) {
      result.push(<em key={key++}>{match[4]}</em>);
    } else if (match[5]) {
      result.push(
        <code key={key++} style={{
          background: 'rgba(124,58,237,0.10)', padding: '1px 5px', borderRadius: 3,
          fontSize: '0.88em', fontFamily: 'monospace', color: COLORS.purple,
        }}>{match[6]}</code>
      );
    }
    lastIndex = pattern.lastIndex;
  }
  if (lastIndex < text.length) result.push(text.slice(lastIndex));
  if (result.length === 0) return text;
  if (result.length === 1 && typeof result[0] === 'string') return result[0];
  return <>{result}</>;
}

function renderMarkdown(text: string): React.ReactNode {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    if (line.startsWith('```')) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      elements.push(
        <pre key={key++} style={{
          background: 'rgba(18,38,170,0.05)', border: `1px solid ${COLORS.border}`,
          borderRadius: 8, padding: '10px 14px', margin: '6px 0',
          fontSize: 12, fontFamily: 'monospace', overflowX: 'auto',
          lineHeight: 1.55, color: COLORS.dark,
        }}>
          <code>{codeLines.join('\n')}</code>
        </pre>
      );
      i++;
      continue;
    }

    // Headings
    if (line.startsWith('### ')) {
      elements.push(<div key={key++} style={{ fontSize: 13, fontWeight: 700, color: COLORS.dark, marginTop: 10, marginBottom: 2 }}>{renderInline(line.slice(4))}</div>);
      i++; continue;
    }
    if (line.startsWith('## ')) {
      elements.push(<div key={key++} style={{ fontSize: 14, fontWeight: 700, color: COLORS.dark, marginTop: 12, marginBottom: 4, borderBottom: `1px solid ${COLORS.border}`, paddingBottom: 4 }}>{renderInline(line.slice(3))}</div>);
      i++; continue;
    }
    if (line.startsWith('# ')) {
      elements.push(<div key={key++} style={{ fontSize: 15, fontWeight: 700, color: COLORS.dark, marginTop: 14, marginBottom: 6 }}>{renderInline(line.slice(2))}</div>);
      i++; continue;
    }

    // Horizontal rule
    if (/^[-*]{3,}$/.test(line.trim())) {
      elements.push(<hr key={key++} style={{ border: 'none', borderTop: `1px solid ${COLORS.border}`, margin: '8px 0' }} />);
      i++; continue;
    }

    // Unordered list — consume consecutive list lines
    if (/^[-*+] /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*+] /.test(lines[i])) {
        items.push(lines[i].slice(2));
        i++;
      }
      elements.push(
        <ul key={key++} style={{ margin: '4px 0 4px 0', paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {items.map((item, j) => (
            <li key={j} style={{ fontSize: 13, lineHeight: 1.6, color: COLORS.dark }}>{renderInline(item)}</li>
          ))}
        </ul>
      );
      continue;
    }

    // Ordered list
    if (/^\d+\. /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\. /.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\. /, ''));
        i++;
      }
      elements.push(
        <ol key={key++} style={{ margin: '4px 0 4px 0', paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {items.map((item, j) => (
            <li key={j} style={{ fontSize: 13, lineHeight: 1.6, color: COLORS.dark }}>{renderInline(item)}</li>
          ))}
        </ol>
      );
      continue;
    }

    // Empty line → small spacer
    if (line.trim() === '') {
      if (elements.length > 0) elements.push(<div key={key++} style={{ height: 6 }} />);
      i++;
      continue;
    }

    // Regular paragraph line
    elements.push(
      <div key={key++} style={{ fontSize: 13, lineHeight: 1.65, color: COLORS.dark }}>
        {renderInline(line)}
      </div>
    );
    i++;
  }

  return <>{elements}</>;
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function SendIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function TypingDots() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 2px', color: COLORS.purple }}>
      <span className="crm-typing-dot" />
      <span className="crm-typing-dot" />
      <span className="crm-typing-dot" />
    </div>
  );
}

function AiAvatar() {
  return (
    <div style={{
      width: 30, height: 30, borderRadius: '50%',
      background: COLORS.purpleSoft,
      border: `1.5px solid rgba(124,58,237,0.18)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      {Icons.sparkle(COLORS.purple, 14)}
    </div>
  );
}

function UserAvatar() {
  return (
    <div style={{
      width: 30, height: 30, borderRadius: '50%',
      background: GRADIENTS.ai,
      boxShadow: '0 2px 6px rgba(18,38,170,0.20)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      {Icons.user('#fff')}
    </div>
  );
}

// ─── Main panel ─────────────────────────────────────────────────────────────

export default function MlpChatPanel({ promptId, promptName, baseApiUrl, onClose }: Props) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalTokens, setTotalTokens] = useState(0);
  const [confirmPending, setConfirmPending] = useState<'reset' | 'close' | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-start the conversation
  useEffect(() => {
    const initialMessage: Message = { role: 'user', content: 'start' };
    setMessages([initialMessage]);
    setLoading(true);
    axios
      .post<ChatResponse>(`${baseApiUrl}/api/ai/chat`, { promptId, messages: [initialMessage] })
      .then(({ data }) => {
        setMessages([initialMessage, { role: 'assistant', content: data.response }]);
        setTotalTokens(data.tokens);
      })
      .catch((err) => setError(apiError(err)))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  async function sendMessage(text: string) {
    const trimmed = text.trim();
    if (!trimmed || loading) return;

    const userMessage: Message = { role: 'user', content: trimmed };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      const { data } = await axios.post<ChatResponse>(`${baseApiUrl}/api/ai/chat`, {
        promptId,
        messages: updatedMessages,
      });
      setMessages((prev) => [...prev, { role: 'assistant', content: data.response }]);
      setTotalTokens((prev) => prev + data.tokens);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  }

  function handleReset() {
    const initialMessage: Message = { role: 'user', content: 'start' };
    setMessages([initialMessage]);
    setInput('');
    setError(null);
    setTotalTokens(0);
    setLoading(true);
    axios
      .post<ChatResponse>(`${baseApiUrl}/api/ai/chat`, { promptId, messages: [initialMessage] })
      .then(({ data }) => {
        setMessages([initialMessage, { role: 'assistant', content: data.response }]);
        setTotalTokens(data.tokens);
      })
      .catch((err) => setError(apiError(err)))
      .finally(() => setLoading(false));
  }

  const visibleMessages = messages.filter((m) => !(m.role === 'user' && m.content === 'start'));
  const canSend = !!input.trim() && !loading;

  return (
    <div
      className="crm-anim-fadeUp"
      style={{
        background: COLORS.white,
        borderRadius: 16,
        border: `1px solid rgba(124,58,237,0.14)`,
        boxShadow: '0 12px 40px rgba(18,38,170,0.10), 0 2px 8px rgba(0,0,0,0.04)',
        marginBottom: 18,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        position: 'relative',
      }}
    >
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div
        style={{
          position: 'relative',
          padding: '14px 18px',
          borderBottom: `1px solid rgba(124,58,237,0.12)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          background: GRADIENTS.aiSoft,
          overflow: 'hidden',
        }}
      >
        {/* Animated gradient left stripe */}
        <div className="crm-gradient-ai" style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4 }} />

        {/* Decorative sparkles */}
        <div style={{ position: 'absolute', top: 10, right: 56, opacity: 0.45, lineHeight: 0, pointerEvents: 'none' }}>
          <SparkleBadge size={18} color={COLORS.purple} />
        </div>
        <div style={{ position: 'absolute', bottom: 8, right: 108, opacity: 0.25, lineHeight: 0, pointerEvents: 'none' }}>
          <SparkleBadge size={12} color={COLORS.blue} />
        </div>

        {/* Left: icon + name + badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, zIndex: 1 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: COLORS.white,
            border: `1px solid rgba(124,58,237,0.18)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: SHADOWS.sm, flexShrink: 0,
          }}>
            {Icons.agent(COLORS.purple)}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: COLORS.dark, lineHeight: 1.2 }}>
              {promptName}
            </div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 3,
              fontSize: 10, fontWeight: 700, color: COLORS.purple,
              letterSpacing: 0.5, textTransform: 'uppercase',
            }}>
              {Icons.sparkle(COLORS.purple, 10)}&nbsp;POWERED BY CLAUDE
            </div>
          </div>
        </div>

        {/* Right: controls */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', zIndex: 1 }}>
          <button
            type="button"
            onClick={() => setConfirmPending('reset')}
            style={{
              padding: '5px 12px', borderRadius: 8,
              border: `1px solid rgba(124,58,237,0.22)`,
              background: COLORS.white,
              color: COLORS.purple, fontSize: 12, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit',
              boxShadow: SHADOWS.sm,
            }}
          >
            New Session
          </button>
          <button
            type="button"
            onClick={() => setConfirmPending('close')}
            aria-label="Close chat"
            style={{
              width: 30, height: 30, borderRadius: 8,
              border: `1px solid ${COLORS.border}`,
              background: COLORS.white,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: COLORS.mid,
              boxShadow: SHADOWS.sm,
            }}
          >
            {Icons.close(COLORS.mid)}
          </button>
        </div>
      </div>

      {/* ── Message list ──────────────────────────────────────────────────── */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '18px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          background: COLORS.pageBg,
        }}
      >
        {visibleMessages.map((m, i) => (
          <div
            key={i}
            className="crm-anim-fadeUp"
            style={{
              display: 'flex',
              flexDirection: m.role === 'user' ? 'row-reverse' : 'row',
              alignItems: 'flex-end',
              gap: 8,
            }}
          >
            {m.role === 'assistant' ? <AiAvatar /> : <UserAvatar />}
            <div
              style={{
                maxWidth: '75%',
                padding: '12px 16px',
                borderRadius: m.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                background: m.role === 'user' ? GRADIENTS.ai : COLORS.white,
                boxShadow: m.role === 'user'
                  ? '0 4px 14px rgba(18,38,170,0.18)'
                  : SHADOWS.md,
                border: m.role === 'assistant' ? `1px solid rgba(124,58,237,0.09)` : 'none',
                wordBreak: 'break-word',
              }}
            >
              {m.role === 'assistant' ? (
                renderMarkdown(m.content)
              ) : (
                <div style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', color: '#fff' }}>
                  {m.content}
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Typing indicator */}
        {loading && (
          <div className="crm-anim-fadeIn" style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
            <AiAvatar />
            <div style={{
              padding: '12px 16px',
              borderRadius: '16px 16px 16px 4px',
              background: COLORS.white,
              border: `1px solid rgba(124,58,237,0.09)`,
              boxShadow: SHADOWS.md,
            }}>
              <TypingDots />
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            padding: '10px 14px',
            background: COLORS.redSoft,
            border: `1px solid ${COLORS.red}30`,
            borderRadius: 10,
            color: COLORS.red,
            fontSize: 13,
          }}>
            {error}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* ── Token count pill ──────────────────────────────────────────────── */}
      {totalTokens > 0 && (
        <div style={{
          padding: '5px 16px',
          fontSize: 11, color: COLORS.mid,
          background: COLORS.white,
          borderTop: `1px solid ${COLORS.border}`,
          flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: 5,
        }}>
          <span style={{ color: COLORS.purple, lineHeight: 0 }}>{Icons.sparkle(COLORS.purple, 10)}</span>
          {totalTokens.toLocaleString()} tokens used this session
        </div>
      )}

      {/* ── Input bar ────────────────────────────────────────────────────── */}
      <div style={{
        padding: '12px 16px',
        borderTop: `1px solid ${COLORS.border}`,
        display: 'flex',
        gap: 10,
        alignItems: 'flex-end',
        flexShrink: 0,
        background: COLORS.white,
      }}>
        {/* Textarea wrapper with focus ring */}
        <div
          className="crm-focus-ring"
          style={{
            flex: 1,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 12,
            background: loading ? COLORS.pageBg : COLORS.white,
            overflow: 'hidden',
            transition: 'border-color 0.15s, box-shadow 0.15s',
          }}
        >
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message… (Enter to send, Shift+Enter for new line)"
            rows={4}
            disabled={loading}
            style={{
              width: '100%',
              padding: '10px 14px',
              border: 'none',
              fontSize: 13,
              fontFamily: 'inherit',
              outline: 'none',
              resize: 'none',
              color: COLORS.dark,
              background: 'transparent',
              lineHeight: 1.55,
              display: 'block',
            }}
          />
        </div>

        {/* Gradient send button */}
        <button
          type="button"
          disabled={!canSend}
          onClick={() => sendMessage(input)}
          aria-label="Send message"
          style={{
            width: 40, height: 40,
            borderRadius: 12,
            border: 'none',
            background: canSend ? GRADIENTS.ai : COLORS.light,
            color: canSend ? '#fff' : COLORS.mid,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: canSend ? 'pointer' : 'not-allowed',
            flexShrink: 0,
            boxShadow: canSend ? SHADOWS.brand : 'none',
            transition: 'all 0.15s',
          }}
        >
          <SendIcon />
        </button>
      </div>

      {/* ── Confirmation overlay ──────────────────────────────────────────── */}
      {confirmPending && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 20,
          background: 'rgba(15,20,50,0.45)',
          backdropFilter: 'blur(3px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div className="crm-anim-fadeUp" style={{
            background: COLORS.white,
            borderRadius: 14,
            border: `1px solid rgba(124,58,237,0.16)`,
            boxShadow: '0 8px 32px rgba(18,38,170,0.14)',
            padding: '24px 28px',
            maxWidth: 340, width: '90%',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 22, marginBottom: 10 }}>
              {confirmPending === 'reset' ? '🔄' : '💬'}
            </div>
            <div style={{ fontWeight: 700, fontSize: 15, color: COLORS.dark, marginBottom: 8 }}>
              {confirmPending === 'reset' ? 'Start a new session?' : 'Close this chat?'}
            </div>
            <div style={{ fontSize: 13, color: COLORS.mid, lineHeight: 1.55, marginBottom: 22 }}>
              {confirmPending === 'reset'
                ? 'Your current conversation will be cleared and a new session will begin. This cannot be undone.'
                : 'Your current conversation will be lost when you close. This cannot be undone.'}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                type="button"
                onClick={() => setConfirmPending(null)}
                style={{
                  padding: '8px 20px', borderRadius: 8,
                  border: `1px solid ${COLORS.border}`,
                  background: COLORS.white, color: COLORS.dark,
                  fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmPending(null);
                  if (confirmPending === 'reset') handleReset();
                  else onClose();
                }}
                style={{
                  padding: '8px 20px', borderRadius: 8,
                  border: 'none',
                  background: confirmPending === 'reset' ? GRADIENTS.ai : COLORS.red,
                  color: '#fff',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                  boxShadow: confirmPending === 'reset' ? SHADOWS.brand : '0 2px 8px rgba(220,38,38,0.25)',
                }}
              >
                {confirmPending === 'reset' ? 'Yes, new session' : 'Yes, close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
